import { type ComponentType } from 'react';
import { type SidePanelWidth } from '@direct-frontend/components';
import { loadable } from '@direct-frontend/modules-render';
import { type PreviewType } from '#shared/lib/previewWidget';
import { type ButtonProps, type PreviewProps } from './types';
import styles from './SidePanelLayer.module.css';

/* LoadableComponent<T> типизирован через внутренний ExtraComponentProps и структурно несовместим с ComponentType<T> из-за поля propTypes */
function asComponent<T>(component: unknown): ComponentType<T> {
    return component as ComponentType<T>;
}

const AddonPreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/AddonPreview'));
const AddonPreviewButtonsLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/AddonPreviewButtons'));

const BundlePreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/BundlePreview'));
const BundlePreviewButtonLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/BundlePreviewButton'));

const LadderPreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/LadderPreview'));
const LadderPreviewButtonLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/LadderPreviewButton'));

const CharacteristicPreviewLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/CharacteristicPreview')
);
const CharacteristicPreviewButtonsLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/CharacteristicPreviewButtons')
);

const CollectionPreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/CollectionPreview'));
const CollectionPreviewButtonsLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/CollectionPreviewButtons')
);

const SystemCollectionPreviewLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/SystemCollectionPreview')
);
const SystemCollectionPreviewButtonsLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/SystemCollectionPreviewButtons')
);

const ContextCollectionPreviewLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/ContextCollectionPreview')
);
const ContextCollectionPreviewButtonsLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/ContextCollectionPreviewButtons')
);

const ConstructorComponentPreviewLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/ConstructorComponentPreview')
);

const FilterCategoryCharacteristicPreviewLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/FilterCategoryCharacteristicPreview')
);

const FilterCollectionCharacteristicPreviewLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/FilterCollectionCharacteristicPreview')
);

const GiftCardPreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/GiftCardPreview'));

const MenuPreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/MenuPreview'));
const MenuPreviewButtonsLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/MenuPreviewButtons'));

const NewsEntryPreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/NewsEntryPreview'));
const NewsEntryPreviewButtonsLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/NewsEntryPreviewButtons')
);

const OrderFormPreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/OrderFormPreview'));
const OrderFormPreviewButtonsLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/OrderFormPreviewButtons')
);

const OrderPreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/OrderPreview'));
const OrderPreviewButtonsLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/OrderPreviewButtons'));

const DiscountPreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/DiscountPreview'));
const DiscountPreviewButtonLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/DiscountPreviewButton')
);
const PromocodePreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/PromocodePreview'));
const PromocodePreviewButtonLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/PromocodePreviewButton')
);

const UserFormPreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/UserFormPreview'));
const UserFormPreviewButtonsLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/UserFormPreviewButtons')
);

const VariantPreviewLazy = loadable(() => import(/* webpackChunkName: "Preview" */ './lazy/VariantPreview'));
const VariantPreviewButtonsLazy = loadable(
    () => import(/* webpackChunkName: "Preview" */ './lazy/VariantPreviewButtons')
);

const GiftPreviewLazy = loadable(() => import(/* webpackChunkName: "GiftPreview" */ './lazy/GiftPreview'));
const GiftPreviewButtonsLazy = loadable(
    () => import(/* webpackChunkName: "GiftPreviewButtons" */ './lazy/GiftPreviewButtons')
);

type PreviewEntry = {
    Preview: ComponentType<PreviewProps>;
    Buttons?: ComponentType<ButtonProps>;
    sidePanelWidth?: SidePanelWidth;
    dismissOnOutsideClick?: boolean;
    className?: string;
};

export const preview: Record<PreviewType, PreviewEntry> = {
    characteristic: {
        Preview: asComponent<PreviewProps>(CharacteristicPreviewLazy),
        Buttons: asComponent<ButtonProps>(CharacteristicPreviewButtonsLazy),
        sidePanelWidth: 's',
    },
    variant: {
        Preview: asComponent<PreviewProps>(VariantPreviewLazy),
        Buttons: asComponent<ButtonProps>(VariantPreviewButtonsLazy),
    },
    order: {
        Preview: asComponent<PreviewProps>(OrderPreviewLazy),
        Buttons: asComponent<ButtonProps>(OrderPreviewButtonsLazy),
    },
    'order-edit': {
        Preview: asComponent<PreviewProps>(OrderFormPreviewLazy),
        Buttons: asComponent<ButtonProps>(OrderFormPreviewButtonsLazy),
    },
    'discount-promocode': {
        Preview: asComponent<PreviewProps>(PromocodePreviewLazy),
        Buttons: asComponent<ButtonProps>(PromocodePreviewButtonLazy),
        sidePanelWidth: 's',
    },
    'discount-automatic': {
        Preview: asComponent<PreviewProps>(DiscountPreviewLazy),
        Buttons: asComponent<ButtonProps>(DiscountPreviewButtonLazy),
        sidePanelWidth: 's',
    },
    'discount-ladder': {
        Preview: asComponent<PreviewProps>(LadderPreviewLazy),
        Buttons: asComponent<ButtonProps>(LadderPreviewButtonLazy),
        sidePanelWidth: 's',
    },
    user: {
        Preview: asComponent<PreviewProps>(UserFormPreviewLazy),
        Buttons: asComponent<ButtonProps>(UserFormPreviewButtonsLazy),
    },
    menu: {
        Preview: asComponent<PreviewProps>(MenuPreviewLazy),
        Buttons: asComponent<ButtonProps>(MenuPreviewButtonsLazy),
        sidePanelWidth: 'm',
    },
    collection: {
        Preview: asComponent<PreviewProps>(CollectionPreviewLazy),
        Buttons: asComponent<ButtonProps>(CollectionPreviewButtonsLazy),
        sidePanelWidth: 's',
    },
    systemCollection: {
        Preview: asComponent<PreviewProps>(SystemCollectionPreviewLazy),
        Buttons: asComponent<ButtonProps>(SystemCollectionPreviewButtonsLazy),
        sidePanelWidth: 's',
    },
    contextCollection: {
        Preview: asComponent<PreviewProps>(ContextCollectionPreviewLazy),
        Buttons: asComponent<ButtonProps>(ContextCollectionPreviewButtonsLazy),
        sidePanelWidth: 's',
    },
    news: {
        Preview: asComponent<PreviewProps>(NewsEntryPreviewLazy),
        Buttons: asComponent<ButtonProps>(NewsEntryPreviewButtonsLazy),
        sidePanelWidth: 's',
    },
    filterCategoryCharacteristic: {
        Preview: asComponent<PreviewProps>(FilterCategoryCharacteristicPreviewLazy),
        sidePanelWidth: 's',
    },
    filterCollectionCharacteristic: {
        Preview: asComponent<PreviewProps>(FilterCollectionCharacteristicPreviewLazy),
        sidePanelWidth: 's',
    },
    giftcard: {
        Preview: asComponent<PreviewProps>(GiftCardPreviewLazy),
        sidePanelWidth: 's',
    },
    'constructor-component': {
        Preview: asComponent<PreviewProps>(ConstructorComponentPreviewLazy),
        dismissOnOutsideClick: false,
        className: styles.constructorComponentPreview,
    },
    'discount-bundle': {
        Preview: asComponent<PreviewProps>(BundlePreviewLazy),
        Buttons: asComponent<ButtonProps>(BundlePreviewButtonLazy),
        sidePanelWidth: 's',
    },
    addon: {
        Preview: asComponent<PreviewProps>(AddonPreviewLazy),
        Buttons: asComponent<ButtonProps>(AddonPreviewButtonsLazy),
        sidePanelWidth: 's',
    },
    gift: {
        Preview: asComponent<PreviewProps>(GiftPreviewLazy),
        sidePanelWidth: 's',
        Buttons: asComponent<ButtonProps>(GiftPreviewButtonsLazy),
    },
};
